import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TextAndPlan from '../../Statement/tabs/TextAndPlan';
import StatementActivity from '../../Statement/tabs/Activity';
import SqlStatistics from '../../Statement/tabs/SQLStatistics';
import Events from '../../Statement/tabs/Events';
import Batches from '../../../component/instance/Tabs/Batches';
import GroupedBatches from '../../../component/instance/Tabs/GroupedBatches';
import InstanceTarget from '../../../types/instance/InstanceTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';
import { TimeRangeContext } from '../../../context/TimeRangeContext';
import { buildApiQuery } from '../../../helpers/timeRangeUtils';
import { FilterContext } from '../../../context/FilterContext';
import { StatementSidePanelContext } from '../../../context/StatementSidePanelContext';
import FilterOption from '../../../types/instance/FilterOption';
import { FilterType } from '../../../typescript/Enums';
import { StatementTypeInfo, createStatementTypeInfo, getPlanAndTextTabName } from './utils';
import { INSTANCE_TYPE_SQLSERVER } from '../../../component/Constants';

interface StatementDetailsPanelProps {
    open: boolean;
    statementId: string | null;
    instance: InstanceTarget;
    onClose: () => void;
    statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch';
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void;
}

// Default empty filter options for the panel's local filter state
const getDefaultFilterOptions = (): FilterOption[] => [
    { filterType: FilterType.Waits, filters: [] },
    { filterType: FilterType.Databases, filters: [] },
    { filterType: FilterType.Sessions, filters: [] },
    { filterType: FilterType.Clients, filters: [] },
    { filterType: FilterType.Users, filters: [] },
    { filterType: FilterType.Programs, filters: [] },
];

const StatementDetailsPanel: React.FC<StatementDetailsPanelProps> = ({ open, statementId, instance, onClose, statementType = 'statement' }) => {
    const [activeTab, setActiveTab] = useState('text');
    const [chartPlanPlotLines, setChartPlanPlotLines] = useState<ChartPlotLine[]>([]);
    const [renderKey, setRenderKey] = useState(0);
    const navigate = useNavigate();
    const timeRangeContext = React.useContext(TimeRangeContext);

    // Overflow tab functionality
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleTabs, setVisibleTabs] = useState<any[]>([]);
    const [overflowTabs, setOverflowTabs] = useState<any[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Create statement type info from the provided type
    const statementTypeInfo: StatementTypeInfo = createStatementTypeInfo(statementType);

    // Local filter state for the panel - isolated from the main page
    const [panelFilterOptions, setPanelFilterOptions] = useState<FilterOption[]>(getDefaultFilterOptions());

    // Reset filters when statement changes
    React.useEffect(() => {
        setRenderKey(prev => prev + 1);
        setPanelFilterOptions(getDefaultFilterOptions());
    }, [statementId]);

    // Callback to update panel's local filter options
    const handleSetFilterOptions = useCallback((options: FilterOption[]) => {
        setPanelFilterOptions(options);
    }, []);

    // Local filter context value for the panel
    const panelFilterContextValue = {
        filters: [],
        setFilters: () => { },
        currentFilterOptions: panelFilterOptions,
        setCurrentFilterOptions: setPanelFilterOptions,
    };

    // Helper function to determine if SQL Statistics should be shown
    const shouldShowSqlStatistics = (): boolean => {
        if (statementTypeInfo.isBatch) {
            return false;
        }
        return true;
    };

    // Dynamic tab configuration based on statement type - memoized to prevent infinite re-renders
    const navItems = useMemo(() => {
        const items = [];

        // Text & Plan tab (always shown)
        items.push({
            label: getPlanAndTextTabName(statementTypeInfo.type, instance).plans,
            icon: 'fal fa-file-code',
            key: 'text',
            show: true,
        });

        // Activity tab (always shown)
        items.push({
            label: getPlanAndTextTabName(statementTypeInfo.type, instance).activity,
            icon: 'fal fa-chart-pie-alt',
            key: 'activity',
            show: true,
        });

        // SQL Statistics tab (conditional)
        items.push({
            label: !statementTypeInfo.isGroup ? 'SQL Statistics' : 'Grouped SQL Statistics',
            icon: 'fal fa-chart-bar',
            key: 'sql-statistics',
            show: shouldShowSqlStatistics(),
        });

        // Batches tab (for Grouped Batch only)
        items.push({
            label: 'Batches',
            icon: 'fal fa-layer-group',
            key: 'batches',
            show: statementTypeInfo.type === 'groupedbatch' && instance.type === INSTANCE_TYPE_SQLSERVER,
        });

        // Calling Batches/Grouped Batches (for Statements and Grouped Statements)
        items.push({
            label: !statementTypeInfo.isGroup ? 'Calling Batches' : 'Calling Grouped Batches',
            icon: 'fal fa-layer-group',
            key: 'batches',
            show: instance.type === INSTANCE_TYPE_SQLSERVER &&
                (statementTypeInfo.type === 'statement' || statementTypeInfo.type === 'groupedstatement'),
        });

        // Events tab (always shown)
        items.push({
            label: 'Events',
            icon: 'fal fa-exchange',
            key: 'events',
            show: true,
        });

        return items.filter(item => item.show);
    }, [statementTypeInfo.type, statementTypeInfo.isGroup, statementTypeInfo.isBatch, instance]);

    // Initialize visible tabs with all navItems as fallback when navItems change
    useEffect(() => {
        if (navItems.length > 0) {
            setVisibleTabs(navItems);
            setOverflowTabs([]);
        } else {
            setVisibleTabs([]);
            setOverflowTabs([]);
        }
    }, [navItems]);

    // Calculate overflow tabs
    useEffect(() => {
        if (!containerRef.current || !measureRef.current || !open) return;

        let frameId: number | null = null;

        const update = () => {
            if (frameId) return;

            frameId = requestAnimationFrame(() => {
                frameId = null;

                if (!containerRef.current || !measureRef.current) return;

                const containerWidth = containerRef.current.offsetWidth;
                const childWidths = Array.from(measureRef.current.children).map(
                    (el) => (el as HTMLElement).offsetWidth
                );

                // If container or children have no width, show all tabs
                if (containerWidth === 0 || childWidths.length === 0 || childWidths.every(w => w === 0)) {
                    setVisibleTabs(navItems);
                    setOverflowTabs([]);
                    return;
                }

                let total = 0;
                const visible: any[] = [];
                const overflow: any[] = [];

                for (let i = 0; i < navItems.length; i++) {
                    if (i < childWidths.length) {
                        total += childWidths[i];
                        if (total + 75 < containerWidth) { // 75px for "More" button
                            visible.push(navItems[i]);
                        } else {
                            overflow.push(navItems[i]);
                        }
                    } else {
                        // If we don't have width for this item, add to overflow
                        overflow.push(navItems[i]);
                    }
                }

                // Ensure at least one tab is visible
                if (visible.length === 0 && navItems.length > 0) {
                    visible.push(navItems[0]);
                    overflow.shift();
                }

                setVisibleTabs(visible);
                setOverflowTabs(overflow);
            });
        };

        // Delay update slightly to ensure panel is fully rendered
        const timeoutId = setTimeout(update, 100);
        update();

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(containerRef.current);

        window.addEventListener("resize", update);

        return () => {
            clearTimeout(timeoutId);
            if (frameId) cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [navItems, open]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Early return after all hooks are called
    if (!statementId) return null;

    return (
        <StatementSidePanelContext.Provider value={true}>
            <div
                aria-hidden={!open}
                tabIndex={-1}
                role="dialog"
                className={`object-browser-details-panel ${open ? '' : 'hidden'}`}
            >
                <div className="panel-header">
                    <span className='header-filters'>
                        <i className="fa fa-file-code" style={{ marginRight: 6 }} aria-hidden="true" />
                        <span>
                            {statementTypeInfo.breadcrumb} Details
                        </span>
                    </span>
                    <div className="control-buttons d-flex align-items-center">
                        <button
                            className="btn btn-outline-secondary me-2"
                            style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                            onClick={() => {
                                onClose();
                                navigate(`/instances/${instance.id}/activity/${statementType}/${statementId}/text?${buildApiQuery(timeRangeContext.timeRange)}`);
                            }}
                        >
                            <i className="fal fa-file-code fa-fw" aria-hidden="true"></i>
                            View {statementTypeInfo.breadcrumb}
                        </button>
                        <button className="btn-close custom-slideout-close-button" onClick={onClose} aria-label="Close panel"></button>
                    </div>
                </div>

                <div className="details-content" key={renderKey}>
                    {/* Hidden measurement div for calculating tab widths */}
                    <div
                        ref={measureRef}
                        style={{
                            position: "absolute",
                            visibility: "hidden",
                            height: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {navItems.map(item => (
                            <button
                                key={`measure-${item.key}`}
                                style={{
                                    fontWeight: 600,
                                    padding: '0.3rem 0.5rem',
                                    fontSize: '1rem',
                                    position: 'relative',
                                    marginRight: '10px',
                                    background: 'none',
                                    border: 'none',
                                    borderRadius: 0,
                                    cursor: 'pointer',
                                    borderBottom: '3px solid #fff',
                                    color: '#000'
                                }}
                            >
                                <i className={`${item.icon} fa-fw`} style={{ marginRight: '5px' }} aria-hidden="true" />
                                {item.label}
                            </button>
                        ))}
                    </div>
                    {/* Visible tabs with overflow dropdown */}
                    <div
                        ref={containerRef}
                        className="menu-top nav nav-tabs"
                        style={{
                            marginRight: 'calc(var(--bs-gutter-x) * .5)',
                            marginLeft: 'calc(var(--bs-gutter-x) * .5)',
                            paddingLeft: 0,
                            marginBottom: '0.75rem',
                            borderBottom: '1px solid var(--dbm-tab-border)',
                            display: "flex",
                            flexWrap: "nowrap",
                            position: "relative",
                        }}
                    >
                        {visibleTabs.map(item => (
                            <button
                                key={item.key}
                                onClick={() => setActiveTab(item.key)}
                                style={{
                                    fontWeight: 600,
                                    padding: '0.3rem 0.5rem',
                                    fontSize: '1rem',
                                    position: 'relative',
                                    marginRight: '10px',
                                    background: 'none',
                                    border: 'none',
                                    borderRadius: 0,
                                    cursor: 'pointer',
                                    borderBottom: activeTab === item.key ? '3px solid var(--dbm-tab-active-underline)' : '3px solid var(--dbm-tab-underline)',
                                    color: 'var(--dbm-tab-text)'
                                }}
                                onMouseEnter={(e) => {
                                    if (activeTab !== item.key) {
                                        e.currentTarget.style.borderBottom = '3px solid var(--dbm-tab-active-underline)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeTab !== item.key) {
                                        e.currentTarget.style.borderBottom = '3px solid var(--dbm-tab-underline)';
                                    }
                                }}
                            >
                                <i className={`${item.icon} fa-fw`} style={{ marginRight: '5px' }} aria-hidden="true" />
                                {item.label}
                            </button>
                        ))}

                        {overflowTabs.length > 0 && (
                            <div className="dropdown" style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    style={{
                                        fontWeight: 600,
                                        padding: '0.3rem 0.5rem',
                                        fontSize: '1rem',
                                        position: 'relative',
                                        marginRight: '10px',
                                        background: 'none',
                                        border: 'none',
                                        borderRadius: 0,
                                        cursor: 'pointer',
                                        borderBottom: '3px solid var(--dbm-tab-underline)',
                                        color: 'var(--dbm-tab-text)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderBottom = '3px solid var(--dbm-tab-active-underline)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderBottom = '3px solid var(--dbm-tab-underline)';
                                    }}
                                >
                                    More
                                    <span className="badge bg-info ms-1">{overflowTabs.length}</span>
                                    <span style={{
                                        display: 'inline-block',
                                        marginLeft: '0.255em',
                                        verticalAlign: '0.255em',
                                        borderTop: '0.3em solid',
                                        borderRight: '0.3em solid transparent',
                                        borderBottom: 0,
                                        borderLeft: '0.3em solid transparent'
                                    }}></span>
                                </button>
                                {dropdownOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        zIndex: 1000,
                                        minWidth: '160px',
                                        padding: '0.25rem 0',
                                        margin: 0,
                                        backgroundColor: 'var(--dbm-dropdown-bg)',
                                        border: '1px solid var(--dbm-border-color-dropdown)',
                                        borderRadius: '0.25rem',
                                        boxShadow: 'var(--dbm-dropdown-shadow)'
                                    }}>
                                        {overflowTabs.map(item => (
                                            <button
                                                key={item.key}
                                                onClick={() => {
                                                    setActiveTab(item.key);
                                                    setDropdownOpen(false);
                                                }}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    padding: '0.25rem 0.75rem',
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                    color: activeTab === item.key ? 'var(--dbm-tab-active-underline)' : 'var(--dbm-tab-text)',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    textAlign: 'left'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#0d6efd';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = activeTab === item.key ? '#0d6efd' : '#000';
                                                }}
                                            >
                                                <i className={`${item.icon} fa-fw`} style={{ marginRight: '5px' }} aria-hidden="true" />
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="tab-content">
                        {activeTab === 'text' && (
                            <TextAndPlan
                                instance={instance}
                                statementId={statementId}
                                setExecutionPlanOptions={setChartPlanPlotLines}
                                statementType={statementTypeInfo}
                            />
                        )}
                        {activeTab === 'activity' && (
                            <FilterContext.Provider value={panelFilterContextValue}>
                                <StatementActivity
                                    eventTypes={[]}
                                    instance={instance}
                                    statementId={statementTypeInfo.isBatch ? undefined : statementId}
                                    batchId={statementTypeInfo.isBatch ? statementId : undefined}
                                   
                                    chartPlanPlotLines={chartPlanPlotLines}
                                    setFilterOptions={handleSetFilterOptions}
                                    tabIdPrefix="panel-"
                                    statementType={statementTypeInfo}
                                />
                            </FilterContext.Provider>
                        )}
                        {activeTab === 'sql-statistics' && (
                            <SqlStatistics
                                instance={instance}
                                filterParameters=""
                               
                                statementId={statementId}
                                statementType={statementTypeInfo}
                            />
                        )}
                        {activeTab === 'batches' && (
                            <>
                                {statementTypeInfo.type === 'groupedbatch' && (
                                    <Batches
                                        instanceId={instance.id}
                                        batchId={statementId}
                                        filtersParameters=""
                                        showByDefault={true}
                                        isBatchesForStatement={true}
                                    />
                                )}
                                {statementTypeInfo.type === 'groupedstatement' && (
                                    <GroupedBatches
                                        instanceId={instance.id}
                                        statementId={statementId}
                                        filtersParameters=""
                                        showByDefault={true}
                                        isBatchesForStatement={true}
                                    />
                                )}
                                {statementTypeInfo.type !== 'groupedbatch' && statementTypeInfo.type !== 'groupedstatement' && (
                                    <Batches
                                        instanceId={instance.id}
                                        statementId={statementId}
                                        filtersParameters=""
                                        showByDefault={true}
                                        isBatchesForStatement={true}
                                    />
                                )}
                            </>
                        )}
                        {activeTab === 'events' && (
                                <Events
                                        instance={instance}
                                       
                                        statementId={statementId}
                                />
                        )}
                    </div>
                </div>
            </div>
        </StatementSidePanelContext.Provider>
    );
};

export default StatementDetailsPanel;